import { toTimezoneDateTime } from '../../lib/date';

export interface FormatTimestampOptions {
  timeZone: string;
  showDate?: boolean;
  showTime?: boolean;
  showTimezone?: boolean;
  showSecondsAndMillis?: boolean;
}

/**
 * Core function to format a timestamp with given options.
 *
 * @param date - The date to format (string, Date, or null/undefined)
 * @param options - Formatting options
 * @returns The formatted timestamp string, or null if date is invalid
 */
export function formatTimestamp(
  date: string | Date | null | undefined,
  options: FormatTimestampOptions,
): string | null {
  const { timeZone, showDate = true, showTime = true, showTimezone = true, showSecondsAndMillis = false } = options;

  if (!date || (!showDate && !showTime)) {
    return null;
  }

  const datetime = toTimezoneDateTime(date, timeZone);

  const dateFormat = 'dd.LL.yyyy';
  const timeFormat = `HH:mm${showSecondsAndMillis ? ':ss.SSS' : ''}`;
  let format;

  if (showDate && showTime) {
    format = `${dateFormat} ${timeFormat}`;
  } else if (showTime && !showDate) {
    format = timeFormat;
  } else if (!showTime && showDate) {
    format = dateFormat;
  } else {
    return null; // Should never happen due to early return above
  }

  let formatted = datetime.toFormat(format);

  if (showTimezone && timeZone !== 'local') {
    formatted += ` (${timeZone})`;
  }

  return formatted;
}

/**
 * Processes a markdown string and replaces timestamp placeholders in the format [[timestamp:ISO_TIMESTAMP]]
 * with formatted dates in the user's timezone.
 *
 * @param markdown - The markdown string that may contain timestamp placeholders like [[timestamp:2025-01-02T10:00:00Z]]
 * @param options - Formatting options
 * @returns The processed markdown string with timestamps replaced
 */
export function formatMarkdownTimestamps(markdown: string, options: FormatTimestampOptions): string {
  // Regex to match [[timestamp:ISO_TIMESTAMP]] pattern
  // Matches: [[ followed by "timestamp:" followed by ISO 8601 timestamp, followed by ]]
  const timestampPattern =
    /\[\[timestamp:([0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(?:\.[0-9]{3})?(?:Z|[+-][0-9]{2}:[0-9]{2})?)\]\]/g;

  return markdown.replace(timestampPattern, (_match, isoString) => {
    const formatted = formatTimestamp(isoString, options);

    return formatted ?? ' - ';
  });
}
